import styled from '@emotion/styled';
import {
  duration,
  easing,
  gradient,
  layout,
  palette,
  radius,
  shadow,
  space,
  spacing,
  status,
  typography,
} from '../../style-utils/theme';
import { Collapse, InlineMessage, SCALE } from '../../motion';
import { touch } from '../../style-utils/media-queries';

/**
 * The pass editor's own styles. Everything else it draws is the form kit
 * (Field, Label, Input, Select, FieldError, FieldHint, HelperText) and the
 * shared primitives (ToggleChip, WeekdayPicker, SegmentedControl, Stepper,
 * CountedTextarea) — these are only the pieces the kit does not have: a group
 * with a visible legend, the live summary, and the form-level refusal.
 * Tokens only; no Emotion component selectors.
 */

/* WHAT QUICK-ADD JUST FILLED. The editor lists the form keys it set in the
   form's data-quick-filled attribute for one painted frame, then clears it:
   the control washes irisTintSoft and settles back through its own colour
   transition. A cue that something changed, never a lasting colour meaning.
   Controls are found by the same name / data-field the server's keys use. */
const QUICK_TARGETS: Record<string, string> = {
  first_name: "[name='first_name']",
  last_name: "[name='last_name']",
  company: "[name='company']",
  category: "[data-field='category']",
  schedule_kind: "[data-field='schedule_kind']",
  valid_from: "[name='valid_from']",
  days: "[data-field='days']",
  interval_weeks: "[name='interval_weeks']",
  any_time: "[aria-label='Time of the visit']",
  window_start: "[name='window_start']",
  window_end: "[name='window_end']",
  valid_until: "[name='valid_until']",
  max_party: "[data-field='max_party']",
  note: "[name='note']",
};

const quickFilledSelector = Object.entries(QUICK_TARGETS)
  .map(([key, target]) => `&[data-quick-filled~='${key}'] ${target}`)
  .join(',\n  ');

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;

  /* Groups have no colour transition of their own; inputs already do. */
  fieldset[data-field],
  [aria-label='Time of the visit'] {
    border-radius: ${radius.xs};
    transition: background-color ${duration.slow} ${easing.standard};
  }

  ${quickFilledSelector} {
    background: ${palette.irisTintSoft};
  }
`;

/* A labelled GROUP of controls (chips, the segmented pill, the stepper). A
   fieldset gives it a real name for assistive tech; the browser chrome is
   reset so it lays out like any other Field. */
export const Fieldset = styled.fieldset`
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin: 0 0 ${spacing.sm};
  padding: 0;
  border: 0;
`;

/* The same type as the form-kit Label, so a group and a field read as one
   form. */
export const Legend = styled.legend`
  margin: 0 0 ${spacing.xs};
  padding: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.inkSoft};
`;

/* The visible asterisk. Decorative: the control itself carries
   aria-required, which is what a screen reader reads. */
export const Required = styled.span`
  margin-left: ${spacing.half};
  color: ${status.alert.fg};
`;

/* The quiet "(optional)" after a label. */
export const Optional = styled.span`
  margin-left: ${spacing.half};
  font-weight: ${typography.weight.regular};
  color: ${palette.slate};
`;

/* Space the focus ring needs. Collapse clips its content (overflow hidden, so
   the height can animate), and a full-width input's 2px ring sits exactly on
   that edge. The body is inset by 4px and the collapse pulled out by the same
   4px, so the ring is drawn inside the clip and nothing moves. Horizontal
   only: a vertical pull would jump the fields below when it unmounts. */
export const Disclosure = styled(Collapse)`
  margin: 0 -${spacing.half};
`;

export const DisclosureBody = styled.div`
  padding: 0 ${spacing.half};
`;

/* THE LIVE SUMMARY — the schedule in one sentence and the next visits. A
   status region, so it is announced when the schedule changes, not on every
   keystroke of a name. */
export const Summary = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
  margin: 0 0 ${spacing.sm};
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surfaceMuted};
`;

export const SummaryLead = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.normal};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const SummaryLine = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

/* The form-level refusal (a 409, a 402, a failure with no field to point
   at): unfolds with its space, role=alert, the alert triple — text on a tint
   that passes. Focusable from code, so a refusal that has no field to send
   the reader to still moves them to the reason. */
export const FormAlertAnchor = styled.div`
  min-width: 0;
  border-radius: ${radius.sm};

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: ${shadow.focusError};
  }
`;

export const FormAlert = styled(InlineMessage)`
  margin: 0 0 ${spacing.sm};
  padding: ${spacing.sm};
  border: 1px solid ${palette.alert};
  border-radius: ${radius.sm};
  background: ${palette.alertSoft};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${status.alert.fg};
`;

/* The slot the resident's quick-add fills (Plan 4); staff leave it empty, so
   it renders nothing there. */
export const QuickAddSlot = styled.div`
  min-width: 0;
  margin: 0 0 ${spacing.sm};
  padding: 0 0 ${spacing.sm};
  border-bottom: 1px solid ${palette.line};
`;

/* The sentence field and its button share a line while they fit; below the
   text floor the button drops under the field instead of squeezing it. */
export const QuickAddRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
`;

export const QuickAddInputWrap = styled.div`
  flex: 1 1 ${space(20)};
  min-width: 0;
`;

/* What quick-add understood, and what it did not. One polite status region,
   so a reader hears the outcome once per press, not per keystroke. */
export const QuickAddStatus = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;

  &:not(:empty) {
    margin-top: ${spacing.xs};
  }
`;

export const QuickAddLine = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

/* THE LOBBERO IQ FALLBACK BUTTON (resident-portal Plan 5, RP-5.8). Aurora,
   because this one IS model output — the reserved AI mark (design_system §2.1),
   the same fill IqCleanupHint's "Use this" wears. White on the aurora is that
   hint's pairing. 44px on touch like every other target. */
export const IqFillButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 ${spacing.sm};
  border: none;
  border-radius: ${radius.sm};
  background: ${gradient.aurora};
  color: ${palette.white};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition: transform ${duration.fast} ${easing.standard}, filter ${duration.fast} ${easing.standard};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(${SCALE.pressed});
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }

  ${touch} {
    min-height: ${layout.touchTarget};
  }
`;

export const IqFillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
  margin-top: ${spacing.xs};
`;
